/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/10/2025 - 14:50:23
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/10/2025
    * - Author          : 
    * - Modification    : 
**/
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { auth } from '../firebase';

export const TUTORIAL_VIDEOS = [
  {
    id: 'tilted',
    title: 'Tilted PV Area Tutorial',
    description: 'This tutorial demonstrates how to create and analyze tilted PV (photovoltaic) areas in PV Glare Check.You’ll learn how to set up a new project, adjust parameters, and generate accurate glare simulation results for tilted solar panel installations.',
    url: "https://firebasestorage.googleapis.com/v0/b/solar-glare.appspot.com/o/demoVideo%2FTiltedPVArea.mp4?alt=media&token=0c740619-148c-410e-8236-f84757c31b96"
  },
  {
    id: 'vertical',
    title: 'Vertical PV Area Tutorial',
    description: 'This tutorial explains how to work with vertical PV installations in PV Glare Check.It covers key considerations and settings for vertical solar panels including façade-mounted systems and vertical sun-tracking installations.',
    url: "https://firebasestorage.googleapis.com/v0/b/solar-glare.appspot.com/o/demoVideo%2FverticalPv.mp4?alt=media&token=e33ba340-c573-44aa-8023-11deef339529"
  }
];

/**
 * Service to handle tutorial videos throughout the app
 */
export const tutorialService = {
  /**
   * Gets all tutorial videos with fresh URLs if possible
   */
  getAllTutorialVideos: async () => {
    try {
      const result = [];
      
      for (const video of TUTORIAL_VIDEOS) {
        try {
          const freshUrl = await tutorialService.getTutorialVideoUrl(video.id);
          result.push({
            ...video,
            url: freshUrl
          });
        } catch (error) {
          console.warn(`Could not get fresh URL for video ${video.id}:`, error);
          result.push({...video});
        }
      }
      
      return result;
    } catch (error) {
      console.error("Error getting tutorial videos:", error);
      return [...TUTORIAL_VIDEOS];
    }
  },

  /**
   * Gets a tutorial video by ID
   */
  getTutorialVideo: async (videoId: string) => {
    const video = TUTORIAL_VIDEOS.find(v => v.id === videoId);
    if (!video) {
      throw new Error(`Tutorial video with ID ${videoId} not found`);
    }
    
    try {
      const freshUrl = await tutorialService.getTutorialVideoUrl(videoId);
      return {
        ...video,
        url: freshUrl
      };
    } catch (error) {
      console.warn(`Could not get fresh URL for video ${videoId}:`, error);
      return video; 
    }
  },
  
  /**
   * Gets a fresh download URL for a tutorial video by ID
   */
  getTutorialVideoUrl: async (videoId: string): Promise<string> => {
    const video = TUTORIAL_VIDEOS.find(v => v.id === videoId);
    if (!video) {
      throw new Error(`Tutorial video with ID ${videoId} not found`);
    }
    
    if (video.url.includes('token=')) {
      return video.url;
    }
    
    try {
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to access videos');
      }
      
      // Extract the filename from the URL
      const urlPath = new URL(video.url).pathname;
      const parts = urlPath.split('/');
      const filename = parts[parts.length - 1];
      
      // Get a fresh URL
      const storage = getStorage();
      const videoRef = ref(storage, `demoVideo/${filename}`);
      const url = await getDownloadURL(videoRef);
      
      return url;
    } catch (error) {
      console.error(`Error getting fresh URL for video ${videoId}:`, error);
      return video.url;
    }
  },
  
  /**
   * Marks a video as "seen" in localStorage
   */
  markVideoAsSeen: (videoId: string) => {
    localStorage.setItem(`solarstripe_demo_video_${videoId}`, 'true');
  },
  
  /**
   * Checks if a video has been marked as "seen"
   */
  isVideoSeen: (videoId: string): boolean => {
    return localStorage.getItem(`solarstripe_demo_video_${videoId}`) === 'true';
  },
  
  /**
   * Reset all "seen" flags for videos (for testing or admin use)
   */
  resetAllVideoFlags: () => {
    TUTORIAL_VIDEOS.forEach(video => {
      localStorage.removeItem(`solarstripe_demo_video_${video.id}`);
    });
  }
};

export default tutorialService;